import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { Box } from '../../../../../../../CommonComponents/OPL/Box/Box';
import { IconButton } from '../../../../../../../CommonComponents/OPL/IconButton/IconButton';
import { InputNumberMask } from '../../../../../../../CommonComponents/OPL/Input/InputNumberMask';
import { Input } from '../../../../../../../CommonComponents/OPL/Input/Input';
import { Label } from '../../../../../../../CommonComponents/OPL/Label/Label';
import { Select } from '../../../../../../../CommonComponents/OPL/Select/Select/Select';
import { SingleDatePicker } from '../../../../../../../CommonComponents/OPL/dates/SingleDatePicker/SingleDatePicker';
import { dateToString } from '../../../../../../../CommonComponents/OPL/dates/utils';
import { Tooltip } from '../../../../../../../CommonComponents/OPL/Tooltip/Tooltip';
import { Icon } from '../../../../../../../CommonComponents/OPL/Icon/Icon';
import { GranTypeDTO } from '../../../../../../../Profile/Accounts/api/useAccountsQuery';
import { OptionsPerGrantTypeDTO } from '../../../../../api/useStockOptionsQuery';
import { useStockOptionsContext } from '../../../../../context/useStockOptionsContext';
import { FormValues, getEarliestDateAfterVesting } from '../../../UpsertGrantModal.meta';
import { FieldsGrid } from '../FieldsGrid';
import { VestingRowContainer } from '../VestingSchedule.styles';

type DispositionExerciseOption = OptionsPerGrantTypeDTO['AvailableDispositionOptions'][number];
type VestingRowValue = FormValues['vestingList'][number] & { key?: string };

interface Props {
  vest: VestingRowValue;
  index: number;
  sharesLabel: string;
  grantType: GranTypeDTO;
  pricePerShare: number | null;
  dispositionOptions: DispositionExerciseOption[] | null;
  exerciseOptions: DispositionExerciseOption[] | null;
  minVestingDate?: Date;
  maxVestingDate?: Date;
  onExerciseStrategyChange: (index: number, value: number | null) => void;
  onDispositionStrategyChange: (index: number, value: number | null) => void;
  onRemove: (index: number) => void;
}

export const VestingRow = ({
  vest,
  index,
  sharesLabel,
  grantType,
  pricePerShare,
  dispositionOptions,
  exerciseOptions,
  minVestingDate,
  maxVestingDate,
  onExerciseStrategyChange,
  onDispositionStrategyChange,
  onRemove,
}: Props) => {
  const { options } = useStockOptionsContext();
  const {
    control,
    formState: { errors },
  } = useFormContext<FormValues>();

  const isIsoOrNso = grantType === GranTypeDTO.Iso || grantType === GranTypeDTO.Nso;
  const vestingDate = vest.VestingDate ? new Date(vest.VestingDate) : new Date();
  const earliestExerciseDate = getEarliestDateAfterVesting(vest.VestingDate).toDate();
  // RSU dispositions keep their original vest-date minimum; the monthly floor is an ISO/NSO rule.
  const earliestDispositionDate = isIsoOrNso ? earliestExerciseDate : vestingDate;
  const selectedExercise = exerciseOptions
    ? exerciseOptions.find(opt => opt.Value === vest.ExerciseStrategy)
    : null;
  const selectedDisposition = dispositionOptions
    ? dispositionOptions.find(opt => opt.Value === vest.DispositionStrategy)
    : null;
  const errorIndex = errors?.vestingList && errors?.vestingList[index];

  return (
    <VestingRowContainer role="listitem">
      <FieldsGrid>
        <Box>
          <Label
            htmlFor={`vestingList-${index}-VestingDate`}
            mb="4"
            mt="2"
            variant={errorIndex?.VestingDate ? 'error' : undefined}
          >
            Vest Date
          </Label>
          <Controller
            control={control}
            name={`vestingList.${index}.VestingDate`}
            render={({ field: { value, onChange, ref } }) => (
              <SingleDatePicker
                id={`vestingList-${index}-VestingDate`}
                inputRef={ref}
                date={value ? new Date(value) : undefined}
                onChange={date => onChange(date ? dateToString(date) : null)}
                maxDate={maxVestingDate ?? new Date(options.LatestVestingDate)}
                minDate={minVestingDate ?? new Date(options.EarliestVestingDate)}
                isWrapperWidth
                formVariant={errorIndex?.VestingDate ? 'error' : undefined}
              />
            )}
          />
        </Box>
        <Controller
          control={control}
          name={`vestingList.${index}.VestedShares`}
          render={({ field: { value, onChange, ...field } }) => (
            <InputNumberMask
              label={sharesLabel}
              {...field}
              value={value}
              allowNegative={false}
              thousandSeparator={false}
              aria-label="Vested shares"
              onValueChange={({ floatValue }) => onChange(floatValue ?? null)}
              formVariant={errorIndex?.VestedShares ? 'error' : undefined}
            />
          )}
        />
        {isIsoOrNso && (
          <Controller
            control={control}
            name={`vestingList.${index}.PricePerShare`}
            render={({ field: { onChange, ...field } }) => (
              <InputNumberMask
                label="Strike Price"
                rightIcon="far fa-dollar-sign"
                decimalScale={2}
                allowNegative={false}
                onValueChange={({ floatValue }) => onChange(floatValue ?? null)}
                {...field}
              />
            )}
          />
        )}
        {isIsoOrNso && (
          <Box>
            <Box display="flex" alignItems="center" mb="4" mt="2">
              <Label mb="0">Bargain Element</Label>
              <Tooltip
                content={
                  grantType === GranTypeDTO.Nso
                    ? 'The bargain element is the difference between the current fair market value and the strike price. For NSOs, this amount is taxed as ordinary income when the shares are exercised.'
                    : 'The bargain element is the difference between the current fair market value and the strike price. For ISOs, this amount is taxed as a long-term capital gain when shares are sold, as long as the disposition is qualifying.'
                }
                placement="top"
              >
                <Icon className="far fa-info-circle" color="neutral.500" fontSize="small" ml="4" />
              </Tooltip>
            </Box>
            <Input
              value={`$${Math.max(
                0,
                (pricePerShare ?? 0) - (vest.PricePerShare ?? 0),
              ).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
              disabled
              aria-label="Bargain element"
            />
          </Box>
        )}
        {exerciseOptions && (
          <>
            <Controller
              name={`vestingList.${index}.ExerciseStrategy`}
              control={control}
              render={({ field: { value, onChange, ref } }) => (
                <Select
                  isClearable
                  innerRef={ref}
                  label="Exercise Strategy"
                  value={
                    value !== null
                      ? {
                          value,
                          label: exerciseOptions.find(opt => opt.Value === value)?.Label,
                        }
                      : null
                  }
                  options={exerciseOptions.map(opt => ({
                    value: opt.Value,
                    label: opt.Label,
                  }))}
                  onChange={option => {
                    onChange(option?.value ?? null);
                    onExerciseStrategyChange(index, option?.value ?? null);
                  }}
                  isPortal
                  formVariant={errorIndex?.ExerciseStrategy ? 'error' : undefined}
                />
              )}
            />
            {selectedExercise && selectedExercise.ShowDateSelector ? (
              <Box>
                <Label
                  htmlFor={`vestingList-${index}-ExerciseDate`}
                  mb="4"
                  variant={errorIndex?.ExerciseDate ? 'error' : undefined}
                >
                  Exercise Date
                </Label>
                <Controller
                  control={control}
                  name={`vestingList.${index}.ExerciseDate`}
                  render={({ field: { value, onChange, ref } }) => (
                    <SingleDatePicker
                      id={`vestingList-${index}-ExerciseDate`}
                      inputRef={ref}
                      date={value ? new Date(value) : undefined}
                      onChange={date => onChange(date ? dateToString(date) : null)}
                      minDate={earliestExerciseDate}
                      openAtMinDate
                      isWrapperWidth
                      formVariant={errorIndex?.ExerciseDate ? 'error' : undefined}
                    />
                  )}
                />
              </Box>
            ) : (
              selectedExercise &&
              selectedExercise.ShowYearSelector && (
                <Controller
                  control={control}
                  name={`vestingList.${index}.ExerciseYear`}
                  render={({ field: { onChange, ...field } }) => (
                    <InputNumberMask
                      label="Years Until Exercise"
                      decimalScale={0}
                      maxLength={2}
                      thousandSeparator={false}
                      allowNegative={false}
                      onValueChange={({ floatValue }) => {
                        onChange(floatValue || '');
                      }}
                      formVariant={errorIndex?.ExerciseYear ? 'error' : undefined}
                      {...field}
                    />
                  )}
                />
              )
            )}
          </>
        )}
        {dispositionOptions && (
          <>
            <Controller
              name={`vestingList.${index}.DispositionStrategy`}
              control={control}
              render={({ field: { value, onChange, ref } }) => (
                <Select
                  isClearable
                  innerRef={ref}
                  label="Disposition Strategy"
                  value={
                    value !== null
                      ? {
                          value,
                          label: dispositionOptions.find(opt => opt.Value === value)?.Label,
                        }
                      : null
                  }
                  options={dispositionOptions.map(opt => ({
                    value: opt.Value,
                    label: opt.Label,
                  }))}
                  onChange={option => {
                    onChange(option?.value ?? null);
                    onDispositionStrategyChange(index, option?.value ?? null);
                  }}
                  isPortal
                  formVariant={errorIndex?.DispositionStrategy ? 'error' : undefined}
                />
              )}
            />
            {selectedDisposition && selectedDisposition.ShowDateSelector ? (
              <Box>
                <Label
                  htmlFor={`vestingList-${index}-DispositionDate`}
                  mb="4"
                  variant={errorIndex?.DispositionDate ? 'error' : undefined}
                >
                  Disposition Date
                </Label>
                <Controller
                  control={control}
                  name={`vestingList.${index}.DispositionDate`}
                  render={({ field: { value, onChange, ref } }) => (
                    <SingleDatePicker
                      id={`vestingList-${index}-DispositionDate`}
                      inputRef={ref}
                      date={value ? new Date(value) : undefined}
                      onChange={date => onChange(date ? dateToString(date) : null)}
                      minDate={earliestDispositionDate}
                      openAtMinDate
                      isWrapperWidth
                      formVariant={errorIndex?.DispositionDate ? 'error' : undefined}
                    />
                  )}
                />
              </Box>
            ) : (
              selectedDisposition &&
              selectedDisposition.ShowYearSelector && (
                <Controller
                  control={control}
                  name={`vestingList.${index}.DispositionYear`}
                  render={({ field: { onChange, ...field } }) => (
                    <InputNumberMask
                      label="Years Until Disposition"
                      decimalScale={0}
                      maxLength={2}
                      thousandSeparator={false}
                      allowNegative={false}
                      onValueChange={({ floatValue }) => onChange(floatValue || '')}
                      formVariant={errorIndex?.DispositionYear ? 'error' : undefined}
                      {...field}
                    />
                  )}
                />
              )
            )}
          </>
        )}
        {isIsoOrNso && (
          <Box>
            <Label htmlFor={`vestingList-${index}-ExpirationDate`} mb="4" mt="2">
              Expiration Date
            </Label>
            <Controller
              control={control}
              name={`vestingList.${index}.ExpirationDate`}
              render={({ field: { value, onChange, ref } }) => (
                <SingleDatePicker
                  id={`vestingList-${index}-ExpirationDate`}
                  inputRef={ref}
                  date={value ? new Date(value) : undefined}
                  onChange={date => onChange(date ? dateToString(date) : null)}
                  isWrapperWidth
                />
              )}
            />
          </Box>
        )}
      </FieldsGrid>
      <IconButton
        icon="far fa-minus-circle"
        onClick={() => onRemove(index)}
        variant="subtleInactive"
        aria-label="Remove"
      />
    </VestingRowContainer>
  );
};
